/**
 * Writer for a flat `{ relativePath: utf8Body }` map — the shape the backend returns
 * for a bundled multi-file payload (today: `wayai admin skill install`).
 */

import * as fs from 'node:fs';
import * as path from 'node:path';

/**
 * A relative path from a server payload becomes an on-disk write target, so it is a
 * path-traversal sink. Reject absolute paths, backslashes, `.`/`..` segments, and
 * anything outside `[A-Za-z0-9._-]`.
 */
export function isSafeRelPath(rel: string): boolean {
  if (rel.length === 0 || rel.length > 300) return false;
  if (rel.startsWith('/') || rel.includes('\\')) return false;
  const segments = rel.split('/');
  return segments.every(
    (seg) => seg.length > 0 && seg !== '.' && seg !== '..' && /^[A-Za-z0-9._-]+$/.test(seg),
  );
}

/**
 * Write a `{ relativePath: body }` map under `targetDir`, creating parent dirs.
 * Re-validates each path (defense-in-depth) — the server already constrained the keys,
 * but the writer is the sink. Returns the written relative paths.
 *
 * Deliberately plain `fs` rather than the symlink-hardened `writeFileNoFollow` in
 * `fs-safety.ts`: that helper's threat model is an attacker-authored workspace checkout,
 * whereas the sole caller (`wayai admin skill install`) writes into the operator's own repo
 * at a locally-validated `<root>/<harness>/skills/<name>` path.
 */
export function writeFileMap(targetDir: string, files: Record<string, string>): string[] {
  const written: string[] = [];
  for (const [rel, body] of Object.entries(files)) {
    if (!isSafeRelPath(rel)) {
      throw new Error(`Refusing to write unsafe path: ${rel}`);
    }
    const abs = path.join(targetDir, rel);
    fs.mkdirSync(path.dirname(abs), { recursive: true });
    fs.writeFileSync(abs, body, 'utf-8');
    written.push(rel);
  }
  return written;
}
